import axios from 'axios';

// Создаем экземпляр axios с настройками
const api = axios.create({
    baseURL: '',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Получить JWT токен из localStorage
const getToken = () => {
  return localStorage.getItem('jwt_token');
};

// Сохранить JWT токен в localStorage
export const setToken = (token) => {
  localStorage.setItem('jwt_token', token);
};

// Удалить JWT токен из localStorage
export const removeToken = () => {
  localStorage.removeItem('jwt_token');
};

// Interceptor для добавления JWT токена к каждому запросу
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Флаг для предотвращения множественных перезагрузок
let isReloading = false;

// Interceptor для обработки ошибок аутентификации
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401) {
      const url = originalRequest?.url || '';

      // НЕ перезагружаем если это запрос к /devices или /auth/check (некритичные)
      if (url.includes('/devices') || url.includes('/auth/check')) {
        return Promise.reject(error);
      }

      // Если это первая попытка - пробуем еще раз
      if (!originalRequest._retry) {
        originalRequest._retry = true;

        console.warn('⚠️ Got 401 error, checking token...');

        try {
          // Проверяем токен
          await api.get('/auth/check');
          console.log('✓ Token is valid, retrying original request...');

          // Повторяем оригинальный запрос
          return api(originalRequest);
        } catch (checkError) {
          console.error('✗ Token check failed, user needs to re-login');
          removeToken();
        }
      }

      // Если retry не помог - перезагружаем страницу
      if (!isReloading) {
        isReloading = true;
        console.error('❌ Authentication failed - reloading page in 1 second...');
        removeToken();
        setTimeout(() => { window.location.reload(); }, 1000);
        setTimeout(() => { isReloading = false; }, 4000);
      }
    }

    return Promise.reject(error);
  }
);


// Auth API
export const authAPI = {
  login: (username, password) =>
    api.post('/auth/login', { username, password }),

  logout: () =>
    api.post('/auth/logout'),

  checkAuth: () =>
    api.get('/auth/check')
};

// Files API
export const filesAPI = {
  getAll: () =>
    api.get('/files'),

  getOne: (name) =>
    api.get(`/files/${name}`),

  create: (name, content = '', group = null) =>
    api.post('/files/create', { name, content, group }),

  update: (name, content) =>
    api.put(`/files/${name}`, { content }),

  delete: (name) =>
    api.delete(`/files/${name}`),

  updateGroup: (name, group) =>
    api.put(`/files/${name}/group`, { group }),

  getSettings: (name) =>
    api.get(`/files/${name}/settings`),

  setMode: (name, mode) =>
    api.put(`/files/${name}/mode`, { mode }),

  setEndDate: (name, endDate) =>
    api.put(`/files/${name}/enddate`, { endDate }),

  // Замок содержимого: файл раздаётся клиентам как обычно, но запись в него запрещена —
  // массовое/групповое редактирование и авто-провижн его пропускают
  setLocked: (name, locked) =>
    api.put(`/files/${name}/locked`, { locked }),

  // Строгий режим привязки: подписку получают только клиенты с install-ID (Happ).
  // Нужен там, где поймали шеринг: Hiddify/V2Box ID не дают, и второе устройство
  // на той же платформе от владельца неотличимо.
  setHappOnly: (name, happOnly) =>
    api.put(`/files/${name}/happ-only`, { happOnly }),

  // Чужие url подписок, привязанные к файлу: их содержимое подмешивается к нашим
  // ссылкам при отдаче /subs, клиент по-прежнему видит один url и один список
  getExternalSubs: (name) =>
    api.get(`/files/${name}/external-subs`),

  setExternalSubs: (name, urls) =>
    api.put(`/files/${name}/external-subs`, { urls }),

  // Проверить чужой url до привязки: сколько ссылок он реально отдаёт
  testExternalSub: (name, url) =>
    api.post(`/files/${name}/external-subs/test`, { url }),

  // Сводка по ВСЕМ привязанным подпискам разом: остаток трафика и срока, отсортировано
  // по тому, что умрёт раньше. Сети не трогает — отдаёт кэш или снимок сторожа.
  externalSubsOverview: () =>
    api.get('/files/external-subs/overview'),

  // Сходить за всеми привязанными источниками мимо кэша и вписать их ссылки в сам файл
  // (ссылки, снятые у источника, при этом уходят и у нас). Возвращает новое содержимое.
  refreshExternalSubs: (name) =>
    api.post(`/files/${name}/external-subs/refresh`),

  // Массовое редактирование
  exportAll: () =>
    api.get('/bulk/export-all'),

  bulkUpdate: (updates) =>
    api.post('/bulk/update-all', { updates })
};

// Devices API — управление UUID-ключами доступа
export const devicesAPI = {
  // Сводка по всем файлам { [filename]: { keyCount, maxDevices } }
  getAll: () =>
    api.get('/devices'),

  // Получить список ключей для файла
  getOne: (filename) =>
    api.get(`/devices/${filename}`),

  // Создать новый UUID-ключ (label — необязательная метка)
  generateKey: (filename, label = '') =>
    api.post(`/devices/${filename}/generate`, { label }),

  // Отозвать конкретный ключ
  revokeKey: (filename, key) =>
    api.delete(`/devices/${filename}/key/${encodeURIComponent(key)}`),

  // Сбросить привязку приложения (User-Agent) для ключа
  unbindKey: (filename, key) =>
    api.post(`/devices/${filename}/key/${encodeURIComponent(key)}/unbind`),

  // Сбросить привязку сразу у всех ключей файла (ключи остаются рабочими)
  unbindAllKeys: (filename) =>
    api.post(`/devices/${filename}/unbind-all`),

  // Сбросить привязки ВО ВСЕХ файлах разом (ключи везде остаются рабочими)
  unbindAllFiles: () =>
    api.post('/devices/unbind-all-files'),

  // Переименовать ключ
  relabelKey: (filename, key, label) =>
    api.put(`/devices/${filename}/key/${encodeURIComponent(key)}/label`, { label }),

  // Отозвать все ключи для файла
  resetAll: (filename) =>
    api.delete(`/devices/${filename}`),

  // Установить лимит ключей для файла
  setLimit: (filename, maxDevices) =>
    api.put(`/devices/${filename}/limit`, { maxDevices }),

  // Crypto4-ссылка, собранная на сервере. Нужна дилерам «только Happ»: у них
  // полного UUID ключа нет, поэтому построить ссылку на клиенте они не могут.
  crypto4: (filename, key) =>
    api.post(`/devices/${filename}/key/${encodeURIComponent(key)}/crypto4`, {}, { timeout: 30000 }),
};

// Биллинг подписок: одна строка на файл подписки, срок хранится в file-settings,
// деньги и история платежей — в billing.json.
export const billingAPI = {
  getOverview: () => api.get('/billing'),
  getOne: (filename) => api.get(`/billing/${filename}`),
  update: (filename, data) => api.put(`/billing/${filename}`, data),
  pay: (filename, data) => api.post(`/billing/${filename}/pay`, data),
  deletePayment: (filename, id) => api.delete(`/billing/${filename}/payment/${id}`),

  // Уровень ключа: у каждого клиента внутри файла свой срок, цена и история платежей
  getKey: (filename, key) =>
    api.get(`/billing/${filename}/key/${encodeURIComponent(key)}`),
  updateKey: (filename, key, data) =>
    api.put(`/billing/${filename}/key/${encodeURIComponent(key)}`, data),
  setKeyEndDate: (filename, key, endDate) =>
    api.put(`/billing/${filename}/key/${encodeURIComponent(key)}/enddate`, { endDate }),
  payKey: (filename, key, data) =>
    api.post(`/billing/${filename}/key/${encodeURIComponent(key)}/pay`, data),
  deleteKeyPayment: (filename, key, id) =>
    api.delete(`/billing/${filename}/key/${encodeURIComponent(key)}/payment/${id}`),
};

// Groups API
export const groupsAPI = {
  getAll: () => api.get('/groups'),
  create: (id, name, type = 'default') => api.post('/groups', { id, name, type }),
  rename: (id, name) => api.put(`/groups/${id}`, { name }),
  setType: (id, type) => api.put(`/groups/${id}/type`, { type }),
  delete: (id) => api.delete(`/groups/${id}`),
};

// VPS API
export const vpsAPI = {
  getAll: () => api.get('/vps'),
  getOne: (id) => api.get(`/vps/${id}`),
  probeStatus: () => api.get('/vps/probe-status'),
  monitorStatus: () => api.get('/vps/monitor/status'),
  monitorStart: () => api.post('/vps/monitor/start'),
  monitorStop: () => api.post('/vps/monitor/stop'),
  monitorRun: () => api.post('/vps/monitor/run'),
  provision: (groupId) => api.post('/vps/provision', { groupId }, { timeout: 600000 }),
  provisionGroup: (groupId) => api.post('/vps/provision-group', { groupId }),
  addManual: (payload) => api.post('/vps/add-manual', payload),
  // Пул: слой открытых в ТМ IP, на которых ещё не стоит VLESS.
  // Квота у HostVDS2 своя в каждом регионе — ручка отдаёт разбивку по регионам.
  getQuota: () => api.get('/vps/quota', { timeout: 60000 }),
  // Один раунд поиска. Отвечает сразу (202), сам раунд идёт минутами — прогресс в /vps/logs.
  poolRound: (region) => api.post('/vps/pool/round', region ? { region } : {}),
  // Пул → рабочий вход. Тоже 202: установка REALITY занимает 2-5 минут.
  setupPool: (id, groupIds) => api.post(`/vps/${id}/setup`, { groupIds }),

  // Gcore. Провижининг долгий (серт + раскатка по PoP до 30 мин), поэтому сервер отвечает 202
  // сразу, а прогресс идёт в /vps/logs — таймаут тут не нужен.
  getGcore: () => api.get('/vps/gcore'),
  provisionGcoreGroup: (groupId, vpsId) => api.post('/vps/provision-gcore-group', vpsId ? { groupId, vpsId } : { groupId }),
  makeGcoreOrigin: (vpsId) => api.post(`/vps/gcore/origin/${vpsId}`),
  // Опрашивает каждый известный вход из ТМ — минуту может думать
  refreshGcore: () => api.post('/vps/gcore/refresh', {}, { timeout: 120000 }),
  deleteGcoreResource: (resourceId) => api.delete(`/vps/gcore/${resourceId}`),
  // Гибрид «1 вход — N серверов»: вписать ещё один VPS в существующий вход. Ссылка клиента
  // не меняется, Gcore начинает балансировать соединения между всеми ориджинами.
  splitGcoreEntry: (resourceId) => api.post(`/vps/gcore/${resourceId}/split`),
  removeGcoreOrigin: (resourceId, vpsId) => api.delete(`/vps/gcore/${resourceId}/origins/${vpsId}`),

  provisionCdnGroup: (groupId) => api.post('/vps/provision-cdn-group', { groupId }),
  provisionCdnAll: () => api.post('/vps/provision-cdn-all'),
  getCdnOrigins: () => api.get('/vps/cdn-origins'),
  // Ходит в API Bunny/Fastly — заметно медленнее остальных ручек, потому тянется отдельно
  getCdnList: () => api.get('/vps/cdn-list', { timeout: 60000 }),
  getEdges: () => api.get('/vps/edges'),
  // Проверка идёт через телефон (до 25с) + TLS-запрос с сервера
  checkEdge: (ip, sni) => api.post('/vps/edges/check', { ip, sni }, { timeout: 60000 }),
  rescanEdges: () => api.post('/vps/edges/rescan'),
  cdnAutoStatus: () => api.get('/vps/cdn-auto/status'),
  cdnAutoStart: () => api.post('/vps/cdn-auto/start'),
  cdnAutoStop: () => api.post('/vps/cdn-auto/stop'),
  setGroup: (id, groupId) => api.put(`/vps/${id}/group`, { groupId }),
  setGroups: (id, groupIds) => api.put(`/vps/${id}/groups`, { groupIds }),
  setCdnGroups: (id, groupIds) => api.put(`/vps/cdn-groups/${id}`, { groupIds }),
  delete: (id) => api.delete(`/vps/${id}`),
  recheck: (id) => api.post(`/vps/${id}/check`, {}, { timeout: 120000 }),
  reinstall: (id, sshPassword) => api.post(`/vps/${id}/reinstall`, sshPassword ? { sshPassword } : {}, { timeout: 600000 }),
  setCredentials: (id, payload) => api.put(`/vps/${id}/credentials`, payload),
  restoreUrl: (id) => api.post(`/vps/${id}/restore-url`),
  restoreUrlAll: (groupId) => api.post('/vps/restore-url-all', groupId ? { groupId } : {}, { timeout: 120000 }),
  getLogs: () => api.get('/vps/logs'),
  sync: () => api.post('/vps/sync'),
  syncVultr: () => api.post('/vps/sync/vultr'),
  syncKamatera: () => api.post('/vps/sync/kamatera'),
  syncHostvds2: () => api.post('/vps/sync/hostvds2'),
  // Синк Bunny гоняет edge через ProbeApp (телефон в ТМ) — это до пары минут, не 30с по умолчанию.
  syncBunny: () => api.post('/vps/sync/bunny', {}, { timeout: 300000 }),
  getCosts: () => api.get('/vps/costs'),
  probeTest: (ip) => api.get('/probe/test', { params: { ip }, timeout: 30000 }),
  autoStatus: () => api.get('/vps/auto/status'),
  autoStart: (groupIds) => api.post('/vps/auto/start', { groupIds }),
  autoStop: () => api.post('/vps/auto/stop'),
};

// Crypto API
export const cryptoAPI = {
  toCrypto4: (url) => api.post('/crypto/crypto4', { url }),
};

// Auto Task API
export const autoTaskAPI = {
  getLogs: (params) => api.get('/auto-task/logs', { params }),
  clearLogs: () => api.delete('/auto-task/logs'),
  getStatus: () => api.get('/auto-task/status'),
  run: () => api.post('/auto-task/run'),
  start: () => api.post('/auto-task/start'),
  stop: () => api.post('/auto-task/stop'),
};

// VanyaVPN Links API — сбор Shadowsocks-ссылок по всем локациям
export const vanyavpnLinksAPI = {
  getLogs: (params) => api.get('/vanyavpn-links/logs', { params }),
  clearLogs: () => api.delete('/vanyavpn-links/logs'),
  getStatus: (accountId, protocol) => api.get('/vanyavpn-links/status', { params: { accountId, protocol } }),
  run: (accountId) => api.post('/vanyavpn-links/run', { accountId }),
  getResults: (accountId) => api.get('/vanyavpn-links/results', { params: { accountId } }),
  verify: (accountId, protocol) => api.post('/vanyavpn-links/verify', { accountId, protocol }),
  getVerified: (accountId, protocol) => api.get('/vanyavpn-links/verified', { params: { accountId, protocol } }),
};

// VanyaVPN Accounts API — управление аккаунтами (JWT-токенами) vanyavpn.com
export const vanyavpnAccountsAPI = {
  getAll: () => api.get('/vanyavpn-accounts'),
  create: (label, jwt) => api.post('/vanyavpn-accounts', { label, jwt }),
  delete: (id) => api.delete(`/vanyavpn-accounts/${id}`),
};

// Gmail Accounts API — управление аккаунтами (app password) для отправки SMS-уведомлений
export const gmailAccountsAPI = {
  getAll: () => api.get('/gmail-accounts'),
  create: (label, email, appPassword) => api.post('/gmail-accounts', { label, email, appPassword }),
  delete: (id) => api.delete(`/gmail-accounts/${id}`),
};

// SMS API — отправка статичного уведомления на softkuram@gmail.com через выбранный gmail-аккаунт
export const smsAPI = {
  send: (accountId) => api.post('/sms/send', { accountId }),
};

// Push API — подписка браузера на уведомления и сторож лимитов чужих подписок.
// checkNow отвечает 202 сразу и НЕ ждёт результата: холодный обход всех источников
// с паузами между запросами идёт минуты, это заведомо больше таймаута axios.
export const pushAPI = {
  vapidKey:    () => api.get('/push/vapid-public-key'),
  subscribe:   (sub) => api.post('/push/subscribe', sub),
  unsubscribe: (endpoint) => api.post('/push/unsubscribe', { endpoint }),
  status:      () => api.get('/push/status'),
  test:        () => api.post('/push/test'),
  checkNow:    () => api.post('/push/check-now'),
};

// Stats API — сводка использования подписок (кто обновлялся, кто молчит, шеринг по IP)
export const statsAPI = {
  get: () => api.get('/stats'),
};

// Dealers API — дилеры (реселлеры): логин/пароль + привязанные группы. Только для админа.
export const dealersAPI = {
  getAll: () => api.get('/dealers'),
  create: (data) => api.post('/dealers', data),
  update: (id, data) => api.put(`/dealers/${id}`, data),
  delete: (id) => api.delete(`/dealers/${id}`),
};

// Clients API
export const clientsAPI = {
  getAll: () => api.get('/clients'),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

// Функция для получения публичной ссылки
// Использует текущий origin — работает и с localhost и с 192.168.x.x через Vite proxy
export const getPublicLink = (filename, key = null) => {
  const base = `${window.location.origin}/subs/${filename}`;
  return key ? `${base}?key=${key}` : base;
};

export default api;

